import { ThreadPrimitive, useAuiState } from "@assistant-ui/react";
import { useRouter } from "@tanstack/react-router";
import { ArrowDown } from "lucide-react";
import { type MouseEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CopilotActionContext } from "@/features/copilot/components/copilot-action-context";
import { CopilotMessage } from "@/features/copilot/components/copilot-message";
import { CopilotThinkingIndicator } from "@/features/copilot/components/copilot-thinking-indicator";
import { HandMark } from "@/features/copilot/components/hand-mark";
import { usePanelMode } from "@/features/copilot/hooks/use-copilot-panel-mode";
import {
  completedAssistantText,
  pendingDecisionAnnouncement,
  runStartAnnouncement,
} from "@/features/copilot/lib/copilot-completion-announcement";
import { copilotErrorKind } from "@/features/copilot/lib/copilot-error-kind";
import {
  copilotInternalNavigationTarget,
  copilotNavigationEffect,
} from "@/features/copilot/lib/copilot-link-navigation";
import {
  type CopilotActionView,
  type CopilotConversation,
  copilotActionDataPartSchema,
} from "@/features/copilot/schemas/copilot.schema";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import { Button } from "@/shared/components/ui/button";
import { UpgradeDialog } from "@/shared/entitlements/components/upgrade-dialog";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

function CompletionAnnouncement({ onRunActivity }: { onRunActivity: (running: boolean) => void }) {
  const running = useAuiState((state) => state.thread.isRunning);
  const messages = useAuiState((state) => state.thread.messages);
  const actions = useContext(CopilotActionContext)?.actions;
  const previousRunning = useRef(running);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const latest = messages.at(-1);
    const was = previousRunning.current;
    const spoken = [
      runStartAnnouncement(was, running),
      completedAssistantText(was, running, latest),
      actions ? pendingDecisionAnnouncement(was, running, latest, actions) : null,
    ].filter(Boolean);
    if (was !== running) onRunActivity(running);
    previousRunning.current = running;
    if (spoken.length > 0) setAnnouncement(spoken.join(". "));
  }, [actions, messages, onRunActivity, running]);

  return (
    <span className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement}
    </span>
  );
}

function StreamErrorNotice() {
  const messages = useAuiState((state) => state.thread.messages);
  const latest = messages.at(-1);
  const error = latest?.status?.type === "incomplete" ? latest.status.error : undefined;
  const errorKind = copilotErrorKind(error);
  const [dismissedError, setDismissedError] = useState<unknown>(null);
  const showUpgrade = errorKind === "plan-upgrade" && dismissedError !== error;

  return (
    <>
      {errorKind === "ai-unavailable" ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 px-3 py-2 text-xs text-destructive"
        >
          Hand is temporarily unavailable. Try again in a little while.
        </p>
      ) : null}
      <UpgradeDialog
        open={showUpgrade}
        onOpenChange={(open) => {
          if (!open) setDismissedError(error);
        }}
        feature={null}
      />
    </>
  );
}

export function CopilotThread({
  conversation,
  onRunActivity,
}: {
  conversation: CopilotConversation;
  onRunActivity: (running: boolean) => void;
}) {
  const actionContext = useContext(CopilotActionContext);
  const { workspace } = useActiveWorkspace();
  const router = useRouter();
  const panelMode = usePanelMode();
  const layout = useCopilotLayout();
  const messages = useAuiState((state) => state.thread.messages);
  const runtimeIsIdle = !useAuiState((state) => state.thread.isRunning);
  const actionViews = useMemo(() => {
    const actions = new Map<string, CopilotActionView>();
    for (const message of messages) {
      for (const part of message.parts) {
        if (part.type !== "data" || part.name !== "copilot-actions") continue;
        const parsed = copilotActionDataPartSchema.safeParse(part.data);
        if (!parsed.success) continue;
        for (const action of parsed.data.actions) actions.set(action.toolCallId, action);
      }
    }
    for (const action of conversation.actions) actions.set(action.toolCallId, action);
    return actions;
  }, [conversation.actions, messages]);
  const latestTurn = conversation.activeTurn;
  const isRunning = latestTurn?.status === "running";

  // Links in answers and cards are plain anchors; a full page load would close Hand.
  function followInAppLinks(event: MouseEvent<HTMLElement>) {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const location = copilotInternalNavigationTarget({
      href: anchor.href,
      currentUrl: window.location.href,
      button: event.button,
      modified: event.ctrlKey || event.metaKey || event.shiftKey || event.altKey,
      hasTarget: anchor.hasAttribute("target"),
      hasDownload: anchor.hasAttribute("download"),
    });
    if (location === null) return;
    event.preventDefault();
    const effect = copilotNavigationEffect(panelMode, layout.isExpanded);
    if (effect === "collapse") layout.toggleExpanded();
    else if (effect === "close") layout.close();
    router.history.push(location);
  }
  return (
    <CopilotActionContext.Provider
      value={{
        workspaceSlug: workspace.slug,
        actions: actionViews,
        canRespond: latestTurn?.status === "waiting_approval" && runtimeIsIdle,
        runtimeIsIdle,
        approvalSending: actionContext?.approvalSending ?? false,
        respondToAction: actionContext?.respondToAction ?? (async () => undefined),
      }}
    >
      <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col" onClick={followInAppLinks}>
        <ThreadPrimitive.Viewport className="scrollbar-thin flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          <div className="mx-auto w-full max-w-3xl flex-1 space-y-4 px-4 pt-3 pb-14">
            {conversation.olderContextOmitted ? (
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-text-secondary">
                Earlier messages were left out to keep this conversation focused.
              </p>
            ) : null}
            {latestTurn?.status === "interrupted" ? (
              <p className="rounded-md border border-border px-3 py-2 text-xs text-text-secondary">
                The previous answer was interrupted. You can continue with a new message.
              </p>
            ) : null}
            {latestTurn?.status === "failed" ? (
              <p className="rounded-md border border-border px-3 py-2 text-xs text-text-secondary">
                The previous answer could not finish. You can try again with a new message.
              </p>
            ) : null}
            <StreamErrorNotice />
            <ThreadPrimitive.Empty>
              <div className="flex min-h-48 flex-col items-center justify-center gap-2 text-center">
                <HandMark className="mb-1 size-10 text-primary dark:text-(--pw-violet-300)" />
                <p className="font-medium">Hi, I'm Hand</p>
                <p className="max-w-xs text-sm text-text-secondary">
                  A helping hand for this workspace. I can look things up, explain them, and prepare
                  changes for you to confirm.
                </p>
              </div>
            </ThreadPrimitive.Empty>
            <ThreadPrimitive.Messages>{() => <CopilotMessage />}</ThreadPrimitive.Messages>
            <CopilotThinkingIndicator serverRunning={isRunning} />
          </div>
          <ThreadPrimitive.ViewportFooter className="pointer-events-none sticky bottom-0 flex justify-center pb-3">
            <ThreadPrimitive.ScrollToBottom asChild>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label="Jump to latest message"
                className="pointer-events-auto rounded-full shadow-md disabled:invisible"
              >
                <ArrowDown aria-hidden="true" />
              </Button>
            </ThreadPrimitive.ScrollToBottom>
          </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Viewport>
        <CompletionAnnouncement onRunActivity={onRunActivity} />
      </ThreadPrimitive.Root>
    </CopilotActionContext.Provider>
  );
}
